"use client";

// Libraries
import CountryFlag, { countryName } from "@/components/ui/CountryFlag/CountryFlag";
import UserAvatar from "@/components/Avatar/UserAvatar";
import AvatarCharacter from "@/components/Avatar/AvatarCharacter";
import ProfileProjects from "@/components/Profile/ProfileProjects";
import { projectList } from "@/helpers/projectList";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import dynamic from "next/dynamic";
import { BACKGROUNDS, isDrawnAvatar } from "@/lib/avatar/catalog";
import React from "react";
import Image from "next/image";
import { Link, useRouter } from "@/i18n/navigation";
import { useForm } from "react-hook-form";
import { useState, useEffect, useRef, Fragment } from "react";
import moment from "moment-timezone";
import { Editor } from "@tinymce/tinymce-react";
import Vimeo from "@u-wave/react-vimeo";
import { getVimeoId } from "@/helpers/vimeoId";
import { getFlagEmoji } from "@/helpers/flagEmoji";

// Components
import UserLayout from "@/components/ui/UserLayout/UserLayout";
import { SpinnerCircular } from "spinners-react";
import { toast } from "react-toastify";
import Layout from "@/components/ui/Layout/Layout";
import ArticleCard from "@/components/ArticleCard/ArticleCard";
import Button from "@/components/ui/Button/Button";
import CourseCard from "@/components/Course/CourseCard/CourseCard";
import Footer from "@/components/ui/Footer/Footer";
import { capitalize } from "@/helpers/capitalize";
import DisplayCourse from "@/components/content/DisplayCourse/DisplayCourse";
import DisplayArticle from "@/components/content/DisplayArticle/DisplayArticle";
import { useSearchParams } from "next/navigation";
import Level from "../../ui/Gamification/Level/Level";
import { useLocale, useTranslations } from "next-intl";
import XpDashboard from "@/components/Charts/XpDashboard/XpDashboard";
import { useSession } from "next-auth/react";
import BebotMascot from "@/components/ui/BebotMascot/BebotMascot";
import { renderMemberHtml } from "@/helpers/renderMemberHtml";

/**
 * Le badge « Mentor » d'un profil reste toujours visible ; il ne devient
 * cliquable que si le mentorat est dans la navigation
 * (MENTORING_IN_NAVIGATION, next.config.mjs).
 */
function LienMentorat({ children }) {
    return process.env.MENTORING_IN_NAVIGATION ? (
        <Link href="/mentoring">{children}</Link>
    ) : (
        <>{children}</>
    );
}

// L'éditeur d'avatar n'est téléchargé que si le membre l'ouvre depuis son profil.
const AvatarComposeModal = dynamic(() => import("@/components/Avatar/AvatarComposeModal"), { ssr: false });

export default function ProfileComponent({ pseudo, ...props }) {
    // Variables
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();
    const router = useRouter();
    const { data: session, status, update } = useSession();
    const searchParams = useSearchParams();
    const section = searchParams.get("section") || searchParams.get("content");
    const locale = useLocale();
    const t = useTranslations();

    // Fonction pour mettre à jour l'URL avec la section - ULTRA RAPIDE
    const updateUrlWithSection = (newSection) => {
        // 1. Mise à jour immédiate de l'URL (synchrone)
        const params = new URLSearchParams(searchParams);
        if (newSection === "about") {
            params.delete("content");
            params.delete("section");
        } else {
            params.set("content", newSection);
            params.delete("section");
        }

        const queryString = params.toString();
        const newUrl = queryString
            ? `${window.location.pathname}?${queryString}`
            : window.location.pathname;

        window.history.replaceState({}, "", newUrl);

        // 2. Mise à jour immédiate de l'état (synchrone)
        setCurrentProfilePage(newSection);

        // 3. Force le re-render immédiat en modifiant directement la classe CSS
        // Sur téléphone, la barre défile : l'onglet choisi revient au milieu.
        document.querySelector(`[data-tab-button="${newSection}"]`)?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
        const buttons = document.querySelectorAll("[data-tab-button]");
        buttons.forEach((btn) => {
            const isActive = btn.dataset.tabButton === newSection;
            btn.classList.toggle("text-gray-900", isActive);
            btn.classList.toggle("dark:text-white", isActive);
            btn.classList.toggle("text-gray-500", !isActive);
            btn.classList.toggle("dark:text-grayDark-400", !isActive);
        });
    };

    // States
    const [isLoading, setIsLoading] = useState(false);
    const [user, setUser] = useState([]);
    const [certifications, setCertifications] = useState([]);
    const [courses, setCourses] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [subsPrograms, setSubsPrograms] = useState([]);
    const [articles, setArticles] = useState([]);
    const [filteredArticles, setFilteredArticles] = useState([]);
    const [articleSortOrder, setArticleSortOrder] = useState("newest"); // "newest" | "oldest"
    const [articleLanguageFilter, setArticleLanguageFilter] = useState("all"); // "all" | "fr" | "en" | etc.
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [currentProfilePage, setCurrentProfilePage] = useState("about");
    const [coursesFromUser, setCoursesFromUser] = useState([]);
    const [projects, setProjects] = useState([]);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [editorKey, setEditorKey] = useState(1);
    const [message, setMessage] = useState("");
    const [messageSending, setMessageSending] = useState(false);
    const [videoId, setVideoId] = useState("");
    const [userLoaded, setUserLoaded] = useState(false);
    const [showBadgesModal, setShowBadgesModal] = useState(false);
    const [showAchievementsModal, setShowAchievementsModal] = useState(false);

    // Les badges du mois forment une série, un par mois. Ceux que l'équipe
    // donne à la main (« Chasseur de bugs »…) sont rangés à part pour ne pas
    // la couper.
    const allBadges = props.data.badges || [];
    const specialBadges = allBadges.filter((one) => one.badge?.isManual);
    const monthlyBadges = allBadges.filter((one) => !one.badge?.isManual);

    // Fonction pour mapper les achievements réels
    const mapAchievements = (achievements) => {
        if (!achievements || !Array.isArray(achievements)) return [];

        return achievements.map((achievement) => ({
            id: achievement.id,
            name:
                achievement.name_translations?.[locale] ||
                achievement.name_translations?.fr ||
                "Achievement",
            description:
                achievement.description_translations?.[locale] ||
                achievement.description_translations?.fr ||
                "",
            imageUrl: achievement.image_url,
            level: achievement.current_level,
            maxLevel: achievement.max_level,
            isLocked: achievement.is_locked,
            progress: achievement.next_level_progress,
            category: achievement.category,
        }));
    };

    // Achievements mappés depuis les données réelles
    const achievements = mapAchievements(props.data?.achievements || []);

    // Methods
    const filterArticlesByLanguage = (articles, locale) => {
        if (!articles || articles.length === 0) return [];

        // Grouper les articles par articleHub.id (si disponible) ou par id comme fallback
        const articleGroups = {};

        articles.forEach((article) => {
            // Utiliser article.articleHub.id s'il existe, sinon utiliser l'id de l'article comme clé de groupement
            const groupKey = article.articleHub?.id || article.id;

            if (!articleGroups[groupKey]) {
                articleGroups[groupKey] = [];
            }
            articleGroups[groupKey].push(article);
        });

        // Pour chaque groupe, sélectionner l'article dans la langue préférée
        const filteredArticles = [];

        Object.values(articleGroups).forEach((group) => {
            if (group.length === 1) {
                // Un seul article dans le groupe, le prendre
                filteredArticles.push(group[0]);
            } else {
                // Plusieurs articles, choisir selon la priorité des langues
                let selectedArticle = null;

                // 1. Chercher d'abord dans la langue actuelle (locale)
                selectedArticle = group.find(
                    (article) =>
                        article.language && article.language.code === locale,
                );

                // 2. Si pas trouvé, chercher en anglais (langue par défaut)
                if (!selectedArticle) {
                    selectedArticle = group.find(
                        (article) =>
                            article.language && article.language.code === "en",
                    );
                }

                // 3. Si toujours pas trouvé, prendre le premier
                if (!selectedArticle) {
                    selectedArticle = group[0];
                }

                filteredArticles.push(selectedArticle);
            }
        });

        return filteredArticles;
    };

    const sortArticles = (articles, sortOrder) => {
        if (!articles || articles.length === 0) return [];

        return [...articles].sort((a, b) => {
            const dateA = new Date(a.dateCreation);
            const dateB = new Date(b.dateCreation);

            if (sortOrder === "newest") {
                return dateB - dateA; // Plus récent en premier
            } else {
                return dateA - dateB; // Plus ancien en premier
            }
        });
    };

    const filterArticlesByLanguageFilter = (articles, languageFilter) => {
        if (!articles || articles.length === 0) return [];
        if (languageFilter === "all") return articles;

        return articles.filter(
            (article) =>
                article.language && article.language.code === languageFilter,
        );
    };

    const applyArticleFiltersAndSort = () => {
        // 1. Appliquer le filtrage par langue
        let filtered = filterArticlesByLanguageFilter(
            articles,
            articleLanguageFilter,
        );

        // 2. Appliquer le tri par date
        filtered = sortArticles(filtered, articleSortOrder);

        setFilteredArticles(filtered);
    };

    const getAvailableLanguagesFromArticles = (articles) => {
        if (!articles || articles.length === 0) return [];

        const languagesSet = new Set();
        articles.forEach((article) => {
            if (article.language && article.language.code) {
                languagesSet.add(article.language.code);
            }
        });

        return Array.from(languagesSet).sort();
    };

    const getLanguageName = (langCode) => {
        const languageNames = {
            fr: "Français",
            en: "English",
            es: "Español",
            de: "Deutsch",
            it: "Italiano",
            pt: "Português",
            ru: "Русский",
            ja: "日本語",
            ko: "한국어",
            zh: "中文",
            ar: "العربية",
            hi: "हिन्दी",
            tr: "Türkçe",
            pl: "Polski",
            nl: "Nederlands",
            sv: "Svenska",
            da: "Dansk",
            no: "Norsk",
            fi: "Suomi",
            cs: "Čeština",
            sk: "Slovenčina",
            hu: "Magyar",
            ro: "Română",
            bg: "Български",
            hr: "Hrvatski",
            sl: "Slovenščina",
            et: "Eesti",
            lv: "Latviešu",
            lt: "Lietuvių",
            el: "Ελληνικά",
            he: "עברית",
            th: "ไทย",
            vi: "Tiếng Việt",
            id: "Bahasa Indonesia",
            ms: "Bahasa Melayu",
            tl: "Filipino",
            sw: "Kiswahili",
            uk: "Українська",
            be: "Беларуская",
            ka: "ქართული",
            hy: "Հայերեն",
            az: "Azərbaycan",
            kk: "Қазақша",
            ky: "Кыргызча",
            uz: "O'zbekcha",
            mn: "Монгол",
        };

        return languageNames[langCode] || langCode.toUpperCase();
    };

    // Version pour les options de select (texte seulement avec emojis)
    const getLanguageDisplayTextForSelect = (langCode) => {
        const name = getLanguageName(langCode);

        // Pour les select, on utilise seulement des emojis (pas de composants React)
        const emojiFlags = {
            fr: "🇫🇷",
            en: "🇺🇸",
            es: "🇪🇸",
            de: "🇩🇪",
            it: "🇮🇹",
            pt: "🇵🇹",
            ru: "🇷🇺",
            zh: "🇨🇳",
            ja: "🇯🇵",
            ko: "🇰🇷",
            ar: "🇸🇦",
            hi: "🇮🇳",
            nl: "🇳🇱",
            sv: "🇸🇪",
            no: "🇳🇴",
            da: "🇩🇰",
            fi: "🇫🇮",
            pl: "🇵🇱",
            tr: "🇹🇷",
            he: "🇮🇱",
            th: "🇹🇭",
            vi: "🇻🇳",
            uk: "🇺🇦",
            cs: "🇨🇿",
            sk: "🇸🇰",
            hu: "🇭🇺",
            ro: "🇷🇴",
            bg: "🇧🇬",
            hr: "🇭🇷",
            sl: "🇸🇮",
            et: "🇪🇪",
            lv: "🇱🇻",
            lt: "🇱🇹",
            id: "🇮🇩",
            ms: "🇲🇾",
            tl: "🇵🇭",
            sw: "🇰🇪",
            ka: "🇬🇪",
            hy: "🇦🇲",
            az: "🇦🇿",
            kk: "🇰🇿",
            ky: "🇰🇬",
            uz: "🇺🇿",
            mn: "🇲🇳",
        };

        const emoji = emojiFlags[langCode];
        return emoji ? `${emoji} ${name}` : name;
    };

    // Version pour l'affichage dans les badges (peut utiliser des composants React)
    const getLanguageDisplayTextForBadge = (langCode) => {
        const flag = getFlagEmoji(langCode);
        const name = getLanguageName(langCode);

        // Si c'est un composant React (SVG), on le retourne tel quel avec le nom
        if (React.isValidElement(flag)) {
            return (
                <span className="inline-flex items-center gap-1">
                    {React.cloneElement(flag, { width: 16, height: 16 })}
                    <span>{name}</span>
                </span>
            );
        }

        // Si c'est un emoji string
        if (typeof flag === "string" && flag !== "🌐") {
            return `${flag} ${name}`;
        }

        // Fallback: juste le nom
        return name;
    };

    const sendMessage = async () => {
        if (!session.user.hasVerifiedEmail) {
            toast.info(t("profile_send_message_notVerifiedEmail"));
            return;
        }

        if (!messageSending && session.user.hasVerifiedEmail) {
            setMessageSending(true);
            const response = await fetch("/api/user/post/message", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    message: message,
                    to: user.id,
                }),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                toast.error(fetchedData.message);
                setMessageSending(false);
            } else {
                toast.success(t("profile_send_message_success"));
                setIsSendingMessage(false);
                setMessage("");
                setEditorKey(editorKey + 1);
            }

            setMessageSending(false);
        }
    };

    const fillUser = () => {
        setUser(props.data.user);
        setCertifications(props.data.certifications);
        setCourses(props.data.courses);
        setPrograms(props.data.programs);
        setSubsPrograms(props.data.subsPrograms);
        setProjects(props.data.projects);

        // Filtrer les articles pour ne montrer qu'une seule langue par articleHub
        const filteredArticles = filterArticlesByLanguage(
            props.data.articles,
            locale,
        );
        setArticles(filteredArticles);

        // Extraire les langues disponibles
        const languages = getAvailableLanguagesFromArticles(filteredArticles);
        setAvailableLanguages(languages);

        // Initialiser les articles filtrés et triés
        const sorted = sortArticles(filteredArticles, articleSortOrder);
        setFilteredArticles(sorted);

        setCoursesFromUser(props.data.coursesCreatedByUser);
        if (props.data.user.portrait) {
            setVideoId(getVimeoId(props.data.user.portrait));
        }
        setUserLoaded(true);
    };
    useEffect(() => {
        fillUser();
    }, []);

    // Réappliquer les filtres et tri quand les options changent
    useEffect(() => {
        if (articles.length > 0) {
            applyArticleFiltersAndSort();
        }
    }, [articleSortOrder, articleLanguageFilter, articles]);

    // Initialisation de l'onglet basé sur l'URL au chargement initial
    useEffect(() => {
        if (section && userLoaded) {
            if (section == "projects") {
                setCurrentProfilePage("projects");
            } else if (section == "articles") {
                setCurrentProfilePage(props.data?.articles?.length ? "articles" : "about");
            } else if (section == "courses") {
                // Vérifier si l'utilisateur a le rôle d'instructeur pour accéder aux formations
                if (user.roles.includes("ROLE_INSTRUCTOR")) {
                    setCurrentProfilePage("courses");
                } else {
                    // Rediriger vers "about" si l'utilisateur n'est pas instructeur et mettre à jour l'URL
                    if (currentProfilePage !== "about") {
                        updateUrlWithSection("about");
                    }
                }
            } else if (section == "about") {
                setCurrentProfilePage("about");
            } else {
                // Section inconnue, rediriger vers "about"
                if (currentProfilePage !== "about") {
                    updateUrlWithSection("about");
                }
            }
        } else if (userLoaded) {
            setCurrentProfilePage("about");
        }
    }, [section, userLoaded, user.roles, currentProfilePage, searchParams]);

    useEffect(() => {
        document.body.style.overflow =
            isSendingMessage || showBadgesModal ? "hidden" : "auto";
    }, [isSendingMessage, showBadgesModal]);

    // La bannière du profil : quand le membre a un avatar, elle prend son fond et montre le
    // personnage en grand, à la place de la couverture et du petit rond. Elle ne remplace jamais
    // une couverture que le membre a mise lui-même, ni le profil d'un mentor ou d'un formateur,
    // où la photo et la couverture gardent la main.
    const bannerRef = useRef(null);
    const defaultCover = !user?.cover || /\/cover\.jpe?g$/.test(user.cover);
    const staffProfile = Array.isArray(user?.roles) && (user.roles.includes("ROLE_MENTOR") || user.roles.includes("ROLE_INSTRUCTOR"));
    const avatarBanner = userLoaded && isDrawnAvatar(user?.avatar) && defaultCover && !staffProfile;
    const bannerColor = avatarBanner ? (BACKGROUNDS.find((one) => one.id === user.avatar.background) || BACKGROUNDS[0]).fill : null;
    // Sur son propre profil, le membre modifie son avatar depuis la bannière.
    const ownProfile = userLoaded && Boolean(session?.user?.userId) && session.user.userId === user?.id;
    const [composing, setComposing] = useState(false);

    // Les projets affichés. Pour un visiteur : ceux que la page a chargés. Sur son propre profil,
    // la liste suit le compte du membre (la session), pour qu'un projet ajouté ou retiré se voie
    // tout de suite, sans attendre que la page publique soit régénérée.
    const [projectCards, setProjectCards] = useState({});
    const ownProjectList = ownProfile && session?.user ? projectList(session.user.projects) : null;
    const shownProjects = ownProjectList
        ? ownProjectList.map((url) => projectCards[url] || (projects || []).find((card) => card.requestUrl === url || card.requestUrl === `https://${url}` || card.requestUrl === `${url}/`) || { requestUrl: url })
        : projects || [];
    const hasProjects = shownProjects.length > 0 || ownProfile;
    const addProject = async (address) => {
        try {
            const response = await fetch("/api/user/post/project", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ project: address }) });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error();
            if (data.card) setProjectCards((cards) => ({ ...cards, [address]: data.card }));
            await update();
            toast.success(t("account_project_added"));
            return true;
        } catch {
            toast.error(t("profile_projects_error"));
            return false;
        }
    };
    const removeProject = async (address) => {
        try {
            const response = await fetch("/api/user/delete/project", { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ project: address }) });
            if (!response.ok) throw new Error();
            await update();
            toast.success(t("account_project_removed"));
        } catch {
            toast.error(t("profile_projects_error"));
        }
    };
    const sessionAvatar = session?.user?.avatar;
    useEffect(() => {
        // L'avatar enregistré arrive par la session : le profil affiché le reprend aussitôt.
        if (ownProfile && isDrawnAvatar(sessionAvatar)) setUser((current) => (JSON.stringify(current.avatar) === JSON.stringify(sessionAvatar) ? current : { ...current, avatar: sessionAvatar }));
    }, [ownProfile, sessionAvatar]);

    // Les onglets du profil : « Projets » pour qui en montre (et toujours sur son propre profil, pour en
    // ajouter), « Articles » seulement pour qui en a écrit, « Formations » pour les formateurs.
    const hasArticles = articles.length > 0;
    const profileTabs = ["about", hasProjects && "projects", hasArticles && "articles", Array.isArray(user?.roles) && user.roles.includes("ROLE_INSTRUCTOR") && "courses"].filter(Boolean);

    // De quel côté la barre d'onglets a encore du contenu caché, pour poser le dégradé de ce bord.
    // La barre est suivie par l'élément lui-même (et non par une ref lue une fois) : s'il est remonté,
    // l'écoute du défilement se rebranche sur le nouvel élément.
    const [tabsBar, setTabsBar] = useState(null);
    const [tabsFade, setTabsFade] = useState({ left: false, right: false });
    useEffect(() => {
        const bar = tabsBar;
        if (!bar) return undefined;
        const measure = () => {
            const left = bar.scrollLeft > 2;
            const right = bar.scrollLeft + bar.clientWidth < bar.scrollWidth - 2;
            setTabsFade((current) => (current.left === left && current.right === right ? current : { left, right }));
        };
        measure();
        bar.addEventListener("scroll", measure, { passive: true });
        const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
        observer?.observe(bar);
        return () => {
            bar.removeEventListener("scroll", measure);
            observer?.disconnect();
        };
    }, [tabsBar, profileTabs.length]);

    let content = "";
    if (userLoaded) {
        content = (
            <div className="max-w-6xl mx-auto lg:pt-14 grid grid-cols-1 xl:grid-cols-12 lg:gap-5 md:px-5 px-0 lg:pb-10">
                {/* Main */}
                <section className="xl:col-span-8">
                    <div>
                        <div>
                            {avatarBanner ? (
                                <div ref={bannerRef} className="relative aspect-[1036/448] overflow-hidden lg:rounded-xl" style={{ background: bannerColor }}>
                                    <AvatarCharacter
                                        avatar={user.avatar}
                                        seed={`member-${user.id}`}
                                        framed={false}
                                        trackWithin={bannerRef}
                                        animated
                                        interactive
                                        reactionLabel={t("avatar_builder_react")}
                                        className="absolute bottom-0 left-1/2 h-[94%] w-auto -translate-x-1/2"
                                        style={{ aspectRatio: "1 / 1", overflow: "visible" }}
                                    />
                                    {ownProfile && (
                                        <button
                                            type="button"
                                            onClick={() => setComposing(true)}
                                            aria-label={t("profile_edit_avatar")}
                                            title={t("profile_edit_avatar")}
                                            className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-xl bg-white/85 text-gray-800 shadow-sm backdrop-blur transition-transform duration-200 hover:scale-105 active:scale-95 sm:right-4 sm:top-4"
                                        >
                                            <SolarIcon name="pen-linear" className="text-xl" />
                                        </button>
                                    )}
                                    {composing && <AvatarComposeModal isOpen onClose={() => setComposing(false)} />}
                                </div>
                            ) : (
                                <div className="aspect-[1036/448] overflow-hidden flex lg:rounded-xl">
                                    {/* Cover */}
                                    <Image
                                        src={
                                            user.cover
                                                ? user.cover
                                                : "/images/site/assets/placeholder-for-cover-profile.jpg"
                                        }
                                        alt={t("profile_cover_alt", {
                                            fullname:
                                                user.pseudo && user.showPseudo
                                                    ? user.pseudo
                                                    : `${user.firstName} ${user.lastName}`,
                                        })}
                                        width={1920}
                                        height={1080}
                                        className="object-cover"
                                        style={{
                                            maxWidth: "100%",
                                            height: "auto",
                                        }}
                                    />
                                </div>
                            )}

                            <div className="relative">
                                {/* Image */}
                                <div className={`transform -translate-y-1/2 rounded-full flex justify-center md:justify-start absolute w-full md:w-auto left-0 md:left-auto md:ml-10 ${avatarBanner ? "hidden" : ""}`}>
                                    <div className="border-4 border-gray-50 dark:border-box rounded-full">
                                        <UserAvatar
                                            animated
                                            interactive
                                            reactionLabel={t("avatar_builder_react")}
                                            user={user}
                                            alt={t("profile_photo_alt", {
                                                fullname:
                                                    user.pseudo &&
                                                    user.showPseudo
                                                        ? user.pseudo
                                                        : `${user.firstName} ${user.lastName}`,
                                            })}
                                            width={150}
                                            height={150}
                                            className="rounded-full object-cover w-24 h-24 sm:w-32 sm:h-32 md:w-[150px] md:h-[150px]"
                                        />
                                    </div>
                                </div>

                                <div className={`${avatarBanner ? "pt-6 px-4 md:px-0" : "md:pt-[90px] sm:pt-20 pt-14 px-0"} pb-5`}>
                                    {/* Full name */}
                                    <h1 className="text-2xl sm:text-3xl font-bold dark:text-white text-center md:text-left">
                                        {user.showPseudo && user.pseudo
                                            ? capitalize(user.pseudo)
                                            : `${capitalize(
                                                  user.firstName,
                                              )} ${capitalize(user.lastName)}`}
                                    </h1>
                                    {/* Pseudo */}
                                    <span className="text-sm text-gray-500 dark:text-lightBlue block text-center md:text-left">
                                        @
                                        {user.pseudo
                                            ? user.pseudo
                                            : `believer-${user.id}`}
                                    </span>
                                    <div className="mt-6 flex flex-wrap items-center gap-2 sm:gap-3 justify-center md:justify-start">
                                        {/* Le pays que le membre a choisi d'afficher : un
                                            drapeau rond et son nom, comme une ligne
                                            d'identité. Il prend la place de la langue,
                                            qui montrait un drapeau français à un
                                            Sénégalais ou à un Québécois. */}
                                        {user.country && (
                                            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-grayDark-800 rounded-full border border-gray-200 dark:border-grayDark-700">
                                                <CountryFlag code={user.country} className="h-5 w-5" />
                                                <span className="text-sm font-medium text-gray-700 dark:text-grayDark-300">
                                                    {countryName(user.country, locale)}
                                                </span>
                                            </div>
                                        )}

                                        {/* Sur son propre profil, tant qu'il n'a pas
                                            choisi : le chemin pour le faire. */}
                                        {ownProfile && !user.country && (
                                            <Link
                                                href="/account"
                                                className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-dashed border-gray-300 dark:border-grayDark-700"
                                            >
                                                <SolarIcon name="global-linear" className="h-4 w-4 text-gray-500 dark:text-grayDark-400" />
                                                <span className="text-sm font-medium text-gray-600 dark:text-grayDark-300">
                                                    {t("profile_add_country")}
                                                </span>
                                            </Link>
                                        )}

                                        {/* Language */}
                                        {!user.country &&
                                            user.language &&
                                            user.language.code && (
                                                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-grayDark-800 rounded-full border border-gray-200 dark:border-grayDark-700">
                                                    <span
                                                        title={
                                                            user.language.name
                                                        }
                                                        className="text-xl"
                                                    >
                                                        {getFlagEmoji(
                                                            user.language.code,
                                                        )}
                                                    </span>
                                                    <span className="text-sm font-medium text-gray-700 dark:text-grayDark-300">
                                                        {user.language.name}
                                                    </span>
                                                </div>
                                            )}

                                        {/* Instructor */}
                                        {user.roles.includes(
                                            "ROLE_INSTRUCTOR",
                                        ) && (
                                            <Link href="/join-instructors">
                                                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-full border border-blue-200 dark:border-blue-800">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 24 24"
                                                        className="text-blue-600 dark:text-blue-400"
                                                    >
                                                        <path
                                                            fill="currentColor"
                                                            d="M14.217 3.5a5.17 5.17 0 0 0-4.434 0L5.489 5.512a2.25 2.25 0 0 1 .647 4.306l-1.076.461c-.534.23-.837.362-1.042.467l-.003.05L9.783 13.5a5.17 5.17 0 0 0 4.434 0l6.691-3.137c1.456-.682 1.456-3.044 0-3.726z"
                                                        />
                                                        <path
                                                            fill="currentColor"
                                                            d="M5 12.915v3.71c0 1.008.503 1.952 1.385 2.44C7.854 19.88 10.204 21 12 21s4.146-1.121 5.615-1.934c.881-.489 1.385-1.433 1.385-2.44v-3.711l-4.146 1.944a6.67 6.67 0 0 1-5.708 0zm.545-4.475a.75.75 0 0 0-.59-1.38l-1.112.477c-.557.239-1.03.441-1.4.65c-.395.222-.734.482-.989.868c-.254.386-.36.8-.408 1.25C1 10.729 1 11.243 1 11.85v2.901a.75.75 0 0 0 1.5 0v-2.862c0-.656.001-1.088.037-1.421c.034-.315.093-.47.17-.586c.075-.115.195-.231.471-.387c.292-.164.689-.335 1.292-.593z"
                                                        />
                                                    </svg>
                                                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                                        {t(
                                                            "profile_instructor",
                                                        )}
                                                    </span>
                                                </div>
                                            </Link>
                                        )}

                                        {/* Mentor */}
                                        {user.roles.includes("ROLE_MENTOR") && (
                                            <LienMentorat>
                                                <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 dark:bg-purple-900/20 rounded-full border border-purple-200 dark:border-purple-800">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 24 24"
                                                        className="text-purple-600 dark:text-purple-400"
                                                    >
                                                        <path
                                                            fill="currentColor"
                                                            fillRule="evenodd"
                                                            d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2S2 6.477 2 12s4.477 10 10 10m1.956-7.905c-.193.17-.44.268-.932.465c-2.31.924-3.465 1.386-4.124.938a1.5 1.5 0 0 1-.398-.398c-.448-.66.014-1.814.938-4.124c.197-.493.295-.74.465-.933q.065-.073.139-.138c.193-.17.44-.268.932-.465c2.31-.924 3.464-1.386 4.124-.938a1.5 1.5 0 0 1 .398.398c.448.66-.014 1.814-.938 4.124c-.197.493-.295.739-.465.932q-.065.075-.139.139"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                    <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                                                        {t("profile_mentor")}
                                                    </span>
                                                </div>
                                            </LienMentorat>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Navigation par onglets. Un onglet vide ne s'affiche pas : « Articles » n'apparaît que
                        pour qui en a écrit, et la barre disparaît quand il ne reste que « À propos ». */}
                    <div className={profileTabs.length > 1 ? "" : "hidden"}>
                        {/* Quand la barre défile, un court dégradé sur le bord dit qu'il y a une suite de ce côté. */}
                        <div className="relative mx-2 overflow-hidden rounded-2xl sm:mx-4 md:mx-0">
                        <div aria-hidden="true" className={`pointer-events-none absolute inset-y-0 left-0 z-20 w-10 bg-gradient-to-r from-gray-50 to-transparent transition-opacity duration-200 dark:from-grayDark-800 ${tabsFade.left ? "opacity-100" : "opacity-0"}`} />
                        <div aria-hidden="true" className={`pointer-events-none absolute inset-y-0 right-0 z-20 w-10 bg-gradient-to-l from-gray-50 to-transparent transition-opacity duration-200 dark:from-grayDark-800 ${tabsFade.right ? "opacity-100" : "opacity-0"}`} />
                        <div ref={setTabsBar} className="scrollbar-hide relative flex gap-1 overflow-x-auto bg-gray-50 p-1 dark:bg-grayDark-800/50">

                            {/* Onglets */}
                            <button
                                data-tab-button="about"
                                onClick={() => updateUrlWithSection("about")}
                                className={`relative z-10 shrink-0 grow whitespace-nowrap py-2.5 sm:py-3 md:py-4 px-3.5 md:px-4 text-sm font-semibold rounded-xl transition-colors duration-200 ease-out group ${
                                    currentProfilePage === "about"
                                        ? "bg-white text-gray-900 shadow-md dark:bg-box dark:text-white"
                                        : "text-gray-500 dark:text-grayDark-400 hover:text-gray-700 dark:hover:text-grayDark-300 hover:bg-white/50 dark:hover:bg-grayDark-700/30 hover:shadow-sm"
                                }`}
                            >
                                <div className="flex items-center justify-center gap-1.5 md:gap-2">
                                    <svg
                                        className="hidden h-4 w-4 flex-shrink-0 sm:block"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                        />
                                    </svg>
                                    <span className="hidden xs:inline">
                                        {t("profile_nav_about")}
                                    </span>
                                    {/* Version courte pour très petits écrans */}
                                    <span className="transition-all duration-300 xs:hidden">
                                        {t("profile_nav_about")}
                                    </span>
                                </div>
                            </button>

                            {hasProjects && (
                                <button
                                    data-tab-button="projects"
                                    onClick={() => updateUrlWithSection("projects")}
                                    className={`relative z-10 shrink-0 grow whitespace-nowrap py-2.5 sm:py-3 md:py-4 px-3.5 md:px-4 text-sm font-semibold rounded-xl transition-colors duration-200 ease-out group ${
                                        currentProfilePage === "projects"
                                            ? "bg-white text-gray-900 shadow-md dark:bg-box dark:text-white"
                                            : "text-gray-500 dark:text-grayDark-400 hover:text-gray-700 dark:hover:text-grayDark-300 hover:bg-white/50 dark:hover:bg-grayDark-700/30 hover:shadow-sm"
                                    }`}
                                >
                                    <div className="flex items-center justify-center gap-1.5 md:gap-2">
                                        <svg
                                            className="hidden h-4 w-4 flex-shrink-0 sm:block"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M3 7.5A2.5 2.5 0 015.5 5h3.38a2 2 0 011.42.59l1.12 1.12a2 2 0 001.41.59h5.67A2.5 2.5 0 0121 9.8v6.7a2.5 2.5 0 01-2.5 2.5h-13A2.5 2.5 0 013 16.5v-9z"
                                            />
                                        </svg>
                                        <span className="hidden xs:inline">
                                            {t("profile_nav_projects")}
                                        </span>
                                        {/* Version courte pour très petits écrans */}
                                        <span className="transition-all duration-300 xs:hidden">
                                            {t("profile_nav_projects")}
                                        </span>
                                        {/* Badge de comptage responsive */}
                                        <span className="bg-primary/20 text-primary text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full transition-all duration-300 group-hover:bg-primary/30 ml-0.5 sm:ml-1 flex-shrink-0">
                                            {shownProjects.length}
                                        </span>
                                    </div>
                                </button>
                            )}
                            {hasArticles && (
                                <button
                                    data-tab-button="articles"
                                    onClick={() => updateUrlWithSection("articles")}
                                    className={`relative z-10 shrink-0 grow whitespace-nowrap py-2.5 sm:py-3 md:py-4 px-3.5 md:px-4 text-sm font-semibold rounded-xl transition-colors duration-200 ease-out group ${
                                        currentProfilePage === "articles"
                                            ? "bg-white text-gray-900 shadow-md dark:bg-box dark:text-white"
                                            : "text-gray-500 dark:text-grayDark-400 hover:text-gray-700 dark:hover:text-grayDark-300 hover:bg-white/50 dark:hover:bg-grayDark-700/30 hover:shadow-sm"
                                    }`}
                                >
                                    <div className="flex items-center justify-center gap-1.5 md:gap-2">
                                        <svg
                                            className="hidden h-4 w-4 flex-shrink-0 sm:block"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                                            />
                                        </svg>
                                        <span className="hidden xs:inline">
                                            {t("profile_nav_articles")}
                                        </span>
                                        {/* Version courte pour très petits écrans */}
                                        <span className="transition-all duration-300 xs:hidden">
                                            {t("profile_nav_articles")}
                                        </span>
                                        {/* Badge de comptage responsive */}
                                        <span className="bg-primary/20 text-primary text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full transition-all duration-300 group-hover:bg-primary/30 ml-0.5 sm:ml-1 flex-shrink-0">
                                            {articles.length}
                                        </span>
                                    </div>
                                </button>
                            )}

                            {/* Onglet Formations - Visible seulement pour les instructeurs */}
                            {user.roles.includes("ROLE_INSTRUCTOR") && (
                                <button
                                    data-tab-button="courses"
                                    onClick={() =>
                                        updateUrlWithSection("courses")
                                    }
                                    className={`relative z-10 shrink-0 grow whitespace-nowrap py-2.5 sm:py-3 md:py-4 px-3.5 md:px-4 text-sm font-semibold rounded-xl transition-colors duration-200 ease-out group ${
                                        currentProfilePage === "courses"
                                            ? "bg-white text-gray-900 shadow-md dark:bg-box dark:text-white"
                                            : "text-gray-500 dark:text-grayDark-400 hover:text-gray-700 dark:hover:text-grayDark-300 hover:bg-white/50 dark:hover:bg-grayDark-700/30 hover:shadow-sm"
                                    }`}
                                >
                                    <div className="flex items-center justify-center gap-1.5 md:gap-2">
                                        <svg
                                            className="hidden h-4 w-4 flex-shrink-0 sm:block"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                                            />
                                        </svg>
                                        <span className="hidden xs:inline">
                                            {t("profile_nav_courses")}
                                        </span>
                                        {/* Version courte pour très petits écrans */}
                                        <span className="transition-all duration-300 xs:hidden">
                                            {t("profile_nav_courses")}
                                        </span>
                                        {/* Badge de comptage responsive */}
                                        <span className="bg-primary/20 text-primary text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full transition-all duration-300 group-hover:bg-primary/30 ml-0.5 sm:ml-1 flex-shrink-0">
                                            {coursesFromUser.length}
                                        </span>
                                    </div>
                                </button>
                            )}
                        </div>
                        </div>
                    </div>

                    {/* Contenu des onglets avec animations */}
                    <div className="relative overflow-hidden md:px-0 px-4">
                        {/* Onglet À propos */}
                        <div
                            className={`transition-all duration-500 ease-out ${
                                currentProfilePage === "about"
                                    ? "opacity-100 translate-x-0 pointer-events-auto"
                                    : "opacity-0 translate-x-8 pointer-events-none absolute inset-0"
                            }`}
                        >
                            {/* Portrait */}
                            {/* {user.portrait && videoId && (
                                <div className="my-10">
                                    <h3 className="text-xl dark:text-white font-bold">
                                        {t("profile_portrait")}
                                    </h3>
                                    <div className="mt-3">
                                        <div className="rounded-xl overflow-hidden">
                                            <Vimeo
                                                video={videoId}
                                                showByline={false}
                                                color="8468e3"
                                                showTitle={false}
                                                showPortrait={false}
                                                speed
                                                className="w-full"
                                                dnt
                                                responsive
                                            />
                                        </div>
                                    </div>
                                </div>
                            )} */}

                            {/* Summary */}
                            <div className="my-10">
                                <h3 className="text-xl dark:text-white font-bold">
                                    {t("profile_summary")}
                                </h3>
                                {user.bio ? (
                                    <div className="mt-3 dark:text-lightBlue text-lg">
                                        {renderMemberHtml(user.bio)}
                                    </div>
                                ) : (
                                    <div className="mt-3 dark:text-lightBlue text-gray-700 italic">
                                        {t("profile_bio_empty")}
                                    </div>
                                )}
                            </div>

                            {/* Liens sociaux */}
                            <div className="my-10">
                                <h3 className="text-xl dark:text-white font-bold">
                                    {t("profile_links")}
                                </h3>
                                {!user.linkTwitter &&
                                    !user.linkYoutube &&
                                    !user.linkLinkedin &&
                                    !user.linkGithub &&
                                    !user.linkWebsite && (
                                        <div className="mt-3 dark:text-lightBlue text-gray-700 italic">
                                            {t("profile_links_empty")}
                                        </div>
                                    )}
                                <div className="mt-3 flex flex-wrap gap-3">
                                    {user.linkTwitter && (
                                        <a
                                            href={user.linkTwitter}
                                            target="_blank"
                                            rel="nofollow"
                                            className="flex items-center gap-1 px-3 py-1.5 border hover:bg-gray-100 dark:hover:bg-bgDark-300 rounded-lg text-black dark:text-lightBlue"
                                        >
                                            <div className="flex items-center justify-center">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    fill="currentColor"
                                                    viewBox="0 0 16 16"
                                                >
                                                    <path
                                                        fill="currentColor"
                                                        d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07l-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z"
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-sm text-gray-500 dark:text-lightBlue truncate">
                                                    X (Twitter)
                                                </div>
                                            </div>
                                        </a>
                                    )}
                                    {user.linkYoutube && (
                                        <a
                                            href={user.linkYoutube}
                                            target="_blank"
                                            rel="nofollow"
                                            className="flex items-center gap-1 px-3 py-1.5 border hover:bg-gray-100 dark:hover:bg-bgDark-300 rounded-lg text-black dark:text-lightBlue"
                                        >
                                            <div className="flex items-center justify-center">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    fill="currentColor"
                                                    viewBox="0 0 16 16"
                                                >
                                                    <path
                                                        fill="currentColor"
                                                        d="M15.8 4.6c-.2-.8-.8-1.4-1.6-1.6C13 2.7 8 2.7 8 2.7s-5 0-6.2.3c-.8.2-1.4.8-1.6 1.6C0 5.8 0 8 0 8s0 2.2.2 3.4c.2.8.8 1.4 1.6 1.6C3 13.3 8 13.3 8 13.3s5 0 6.2-.3c.8-.2 1.4-.8 1.6-1.6.2-1.2.2-3.4.2-3.4s0-2.2-.2-3.4zM6.4 10.5V5.5l4.3 2.5-4.3 2.5z"
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-sm text-gray-500 dark:text-lightBlue truncate">
                                                    YouTube
                                                </div>
                                            </div>
                                        </a>
                                    )}
                                    {user.linkLinkedin && (
                                        <a
                                            href={user.linkLinkedin}
                                            target="_blank"
                                            rel="nofollow"
                                            className="flex items-center gap-1 px-3 py-1.5 border hover:bg-gray-100 dark:hover:bg-bgDark-300 rounded-lg text-black dark:text-lightBlue"
                                        >
                                            <div className="flex items-center justify-center">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    fill="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        fill="currentColor"
                                                        d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93zM6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37z"
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-sm text-gray-500 dark:text-lightBlue truncate">
                                                    LinkedIn
                                                </div>
                                            </div>
                                        </a>
                                    )}
                                    {user.linkGithub && (
                                        <a
                                            href={user.linkGithub}
                                            target="_blank"
                                            rel="nofollow"
                                            className="flex items-center gap-1 px-3 py-1.5 border hover:bg-gray-100 dark:hover:bg-bgDark-300 rounded-lg text-black dark:text-lightBlue"
                                        >
                                            <div className="flex items-center justify-center">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    fill="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4.44c-.32-.07-.33-.68-.33-.89l.01-2.47c0-.84-.29-1.39-.61-1.67c2.01-.22 4.11-.97 4.11-4.44c0-.98-.35-1.79-.92-2.42c.09-.22.4-1.14-.09-2.38c0 0-.76-.23-2.48.93c-.72-.2-1.48-.3-2.25-.31c-.76.01-1.54.11-2.25.31c-1.72-1.16-2.48-.93-2.48-.93c-.49 1.24-.18 2.16-.09 2.38c-.57.63-.92 1.44-.92 2.42c0 3.47 2.1 4.22 4.1 4.47c-.26.2-.49.6-.57 1.18c-.52.23-1.82.63-2.62-.75c0 0-.48-.86-1.38-.93c0 0-.88 0-.06.55c0 0 .59.28 1 1.32c0 0 .52 1.75 3.03 1.21l.01 1.53c0 .21-.02.82-.34.89H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-sm text-gray-500 dark:text-lightBlue truncate">
                                                    GitHub
                                                </div>
                                            </div>
                                        </a>
                                    )}
                                    {user.linkWebsite && (
                                        <a
                                            href={user.linkWebsite}
                                            target="_blank"
                                            rel="nofollow"
                                            className="flex items-center gap-1 px-3 py-1.5 border hover:bg-gray-100 dark:hover:bg-bgDark-300 rounded-lg text-black dark:text-lightBlue"
                                        >
                                            <div className="flex items-center justify-center">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    fill="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <g
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                    >
                                                        <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0-18 0m.6-3h16.8M3.6 15h16.8" />
                                                        <path d="M11.5 3a17 17 0 0 0 0 18m1-18a17 17 0 0 1 0 18" />
                                                    </g>
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-sm text-gray-500 dark:text-lightBlue truncate">
                                                    {t("profile_website")}
                                                </div>
                                            </div>
                                        </a>
                                    )}
                                </div>
                            </div>


                            {/* Summary XPs between him and user if connected */}
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-10">
                                {t("xpDashboard.title")}
                            </h2>
                            {status !== "loading" && (
                                <XpDashboard
                                    users={
                                        session &&
                                        session.user &&
                                        session.user.userId &&
                                        session.user.userId !== user.id
                                            ? [session.user.userId, user.id]
                                            : [user.id]
                                    }
                                    userTimezone={
                                        session &&
                                        session.user &&
                                        session.user.timezone
                                            ? session.user.timezone
                                            : null
                                    }
                                    className="mt-3"
                                />
                            )}

                            {/* Certifications */}
                            <div className="mt-10">
                                <h3 className="text-xl dark:text-white font-bold">
                                    {t("profile_certifications")}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 mt-3 gap-4">
                                    {certifications.map((certification) => (
                                        <div
                                            className="col-span-1"
                                            key={certification.id}
                                        >
                                            <Link
                                                href={`/certifications/${certification.certificationNumber}`}
                                            >
                                                <div className="relative bg-gradient-to-br from-white to-gray-50 dark:from-grayDark-800 dark:to-bgDark-300 border border-gray-200 dark:border-bgDark-400 hover:border-primary dark:hover:border-primary p-6 hover:shadow-lg dark:hover:shadow-2xl duration-300 rounded-2xl h-full flex flex-col overflow-hidden group">
                                                    {/* Logo Believemy Certified style tampon en arrière-plan */}
                                                    <div
                                                        className="absolute top-1/2 left-1/3 transform -translate-y-1/2 opacity-5 group-hover:opacity-10 transition-all duration-500 rotate-12 group-hover:rotate-6 pointer-events-none"
                                                        style={{
                                                            width: "300px",
                                                            height: "300px",
                                                            left: "70%",
                                                            top: "90%",
                                                            marginLeft:
                                                                "-100px",
                                                            marginTop: "-100px",
                                                            border: 0,
                                                        }}
                                                    >
                                                        <img
                                                            src="/images/site/brand/believemy-certified-full-black.svg"
                                                            alt="Believemy Certified"
                                                            className="w-[300px] h-[300px] dark:hidden filter grayscale"
                                                            style={{
                                                                position:
                                                                    "absolute",
                                                                left: 0,
                                                                top: 0,
                                                                objectFit:
                                                                    "contain",
                                                                border: 0,
                                                            }}
                                                        />
                                                        <img
                                                            src="/images/site/brand/believemy-certified-full-white.svg"
                                                            alt="Believemy Certified"
                                                            className="w-[300px] h-[300px] hidden dark:block filter grayscale"
                                                            style={{
                                                                position:
                                                                    "absolute",
                                                                left: 0,
                                                                top: 0,
                                                                objectFit:
                                                                    "contain",
                                                                border: 0,
                                                            }}
                                                        />
                                                    </div>

                                                    {/* Badge de certification */}
                                                    <div className="flex items-center mb-4">
                                                        <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full mr-3">
                                                            <svg
                                                                className="w-4 h-4 text-primary"
                                                                fill="currentColor"
                                                                viewBox="0 0 20 20"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        </div>
                                                        <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded-full">
                                                            {t(
                                                                "profile_certification_badge",
                                                            )}
                                                        </span>
                                                    </div>

                                                    {/* Nom du cours */}
                                                    <div className="flex-grow">
                                                        <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2 leading-tight group-hover:text-primary transition-colors duration-300">
                                                            {
                                                                certification
                                                                    .course.name
                                                            }
                                                        </h4>
                                                        <p className="text-sm text-gray-600 dark:text-textSecondDark">
                                                            {t(
                                                                "profile_certification_course",
                                                            )}
                                                        </p>
                                                    </div>

                                                    {/* Date et numéro de certification */}
                                                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-bgDark-400">
                                                        <div className="text-xs text-gray-500 dark:text-textSecondDark mb-1">
                                                            {t(
                                                                "profile_certification_delivered",
                                                                {
                                                                    date: moment(
                                                                        certification.dateCreation,
                                                                    )
                                                                        .locale(
                                                                            locale,
                                                                        )
                                                                        .format(
                                                                            "LL",
                                                                        ),
                                                                },
                                                            )}
                                                        </div>
                                                        <div className="text-xs font-mono text-gray-400 dark:text-textSecondDark">
                                                            #
                                                            {
                                                                certification.certificationNumber
                                                            }
                                                        </div>
                                                    </div>

                                                    {/* Indicateur visuel de hover */}
                                                    <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                                                </div>
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                                {certifications.length == 0 && (
                                    <div className="dark:text-textSecondDark text-gray-700 italic">
                                        {t("profile_certifications_empty")}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Onglet Projets */}
                        <div
                            className={`transition-all duration-500 ease-out ${
                                currentProfilePage === "projects"
                                    ? "opacity-100 translate-x-0 pointer-events-auto"
                                    : "opacity-0 translate-x-8 pointer-events-none absolute inset-0"
                            }`}
                        >
                            <ProfileProjects projects={shownProjects} own={ownProfile} onAdd={addProject} onRemove={removeProject} className="my-8" />
                        </div>

                        {/* Onglet Articles */}
                        <div
                            className={`transition-all duration-500 ease-out ${
                                currentProfilePage === "articles"
                                    ? "opacity-100 translate-x-0 pointer-events-auto"
                                    : "opacity-0 translate-x-8 pointer-events-none absolute inset-0"
                            }`}
                        >
                            <div className="my-10">
                                <div className="flex flex-col gap-4 mb-6">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-xl dark:text-white font-bold">
                                            {t("profile_articles")}
                                        </h3>
                                        <span className="text-sm font-medium px-3 py-1 rounded-full bg-primary/10 text-primary dark:bg-primary/20">
                                            {t("profile_articles_count", {
                                                count: filteredArticles.length,
                                            })}
                                        </span>
                                    </div>

                                    {/* Contrôles de tri et filtrage */}
                                    {articles.length > 0 && (
                                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-borderDark/20 dark:to-borderDark/40 p-5 rounded-xl border border-gray-200 dark:border-borderDark shadow-sm">
                                            <div className="flex flex-wrap items-center gap-6">
                                                {/* Tri par date */}
                                                <div className="flex items-center gap-3">
                                                    <div className="flex items-center gap-2">
                                                        <svg
                                                            className="w-4 h-4 text-gray-600 dark:text-grayDark-400"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                                                            />
                                                        </svg>
                                                        <label className="text-sm font-semibold text-gray-700 dark:text-grayDark-300">
                                                            {t(
                                                                "profile_sort_by",
                                                            )}
                                                        </label>
                                                    </div>
                                                    <select
                                                        value={articleSortOrder}
                                                        onChange={(e) =>
                                                            setArticleSortOrder(
                                                                e.target.value,
                                                            )
                                                        }
                                                        className="text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-borderDark bg-white dark:bg-borderDark/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-200 hover:border-primary/30 shadow-sm"
                                                    >
                                                        <option value="newest">
                                                            📅{" "}
                                                            {t(
                                                                "profile_sort_by_newest",
                                                            )}
                                                        </option>
                                                        <option value="oldest">
                                                            🗓️{" "}
                                                            {t(
                                                                "profile_sort_by_oldest",
                                                            )}
                                                        </option>
                                                    </select>
                                                </div>

                                                {/* Filtre par langue */}
                                                {availableLanguages.length >
                                                    1 && (
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex items-center gap-2">
                                                            <svg
                                                                className="w-4 h-4 text-gray-600 dark:text-grayDark-400"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                                                                />
                                                            </svg>
                                                            <label className="text-sm font-semibold text-gray-700 dark:text-grayDark-300">
                                                                {t(
                                                                    "profile_language",
                                                                )}
                                                            </label>
                                                        </div>
                                                        <select
                                                            value={
                                                                articleLanguageFilter
                                                            }
                                                            onChange={(e) =>
                                                                setArticleLanguageFilter(
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            className="text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-borderDark bg-white dark:bg-borderDark/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-200 hover:border-primary/30 shadow-sm"
                                                        >
                                                            <option value="all">
                                                                🌍{" "}
                                                                {t(
                                                                    "profile_language_all",
                                                                )}
                                                            </option>
                                                            {availableLanguages.map(
                                                                (langCode) => (
                                                                    <option
                                                                        key={
                                                                            langCode
                                                                        }
                                                                        value={
                                                                            langCode
                                                                        }
                                                                    >
                                                                        {getLanguageDisplayTextForSelect(
                                                                            langCode,
                                                                        )}
                                                                    </option>
                                                                ),
                                                            )}
                                                        </select>
                                                    </div>
                                                )}

                                                {/* Compteur avec filtres actifs */}
                                                <div className="ml-auto">
                                                    {(articleLanguageFilter !==
                                                        "all" ||
                                                        filteredArticles.length !==
                                                            articles.length) && (
                                                        <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 dark:bg-primary/20 rounded-lg">
                                                            <svg
                                                                className="w-4 h-4 text-primary"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                                                />
                                                            </svg>
                                                            <span className="text-xs font-medium text-primary">
                                                                {
                                                                    filteredArticles.length
                                                                }{" "}
                                                                sur{" "}
                                                                {
                                                                    articles.length
                                                                }{" "}
                                                                articles
                                                            </span>
                                                        </div>
                                                    )}
                                                    {articleLanguageFilter ===
                                                        "all" &&
                                                        filteredArticles.length ===
                                                            articles.length && (
                                                            <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-borderDark/30 rounded-lg">
                                                                <svg
                                                                    className="w-4 h-4 text-gray-500 dark:text-grayDark-400"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth={
                                                                            2
                                                                        }
                                                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                                    />
                                                                </svg>
                                                                <span className="text-xs font-medium text-gray-600 dark:text-grayDark-400">
                                                                    {t(
                                                                        "profile_articles_total",
                                                                        {
                                                                            count: articles.length,
                                                                        },
                                                                    )}
                                                                </span>
                                                            </div>
                                                        )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {filteredArticles.length == 0 &&
                                    articles.length == 0 && (
                                        <div className="text-center py-12">
                                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-borderDark flex items-center justify-center">
                                                <svg
                                                    className="w-8 h-8 text-gray-400 dark:text-textSecondDark"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                    />
                                                </svg>
                                            </div>
                                            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                                {t("profile_articles_empty")}
                                            </h4>
                                            <p className="text-gray-500 dark:text-textSecondDark">
                                                {t(
                                                    "profile_articles_empty_description",
                                                )}
                                            </p>
                                        </div>
                                    )}

                                {/* Message si aucun article après filtrage */}
                                {filteredArticles.length == 0 &&
                                    articles.length > 0 && (
                                        <div className="text-center py-8">
                                            <div className="text-gray-500 dark:text-textSecondDark">
                                                {t(
                                                    "profile_articles_empty_description_filtered",
                                                )}
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setArticleLanguageFilter(
                                                        "all",
                                                    );
                                                    setArticleSortOrder(
                                                        "newest",
                                                    );
                                                }}
                                                className="mt-2 text-sm text-primary hover:text-primary/80 underline"
                                            >
                                                {t("profile_reset_filters")}
                                            </button>
                                        </div>
                                    )}

                                {filteredArticles.length > 0 && (
                                    <div className="space-y-4">
                                        {filteredArticles.map(
                                            (article, index) => (
                                                <div
                                                    key={`article-${article.id}`}
                                                    className="group relative bg-white dark:bg-borderDark/20 rounded-xl border border-gray-200 dark:border-borderDark hover:border-primary/30 dark:hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 overflow-hidden"
                                                >
                                                    <DisplayArticle
                                                        article={article}
                                                    />

                                                    {/* Indicateur de langue si disponible */}
                                                    {article.language && (
                                                        <div className="absolute top-4 right-4 z-10">
                                                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-gray-100 dark:bg-borderDark text-gray-700 dark:text-textSecondDark">
                                                                {getLanguageDisplayTextForBadge(
                                                                    article
                                                                        .language
                                                                        .code,
                                                                )}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* Bordure animée au hover */}
                                                    <div
                                                        className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/20 to-secondary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                                                        style={{
                                                            background:
                                                                "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)",
                                                        }}
                                                    ></div>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Onglet Formations - Visible seulement pour les instructeurs */}
                        {user.roles.includes("ROLE_INSTRUCTOR") && (
                            <div
                                className={`transition-all duration-500 ease-out ${
                                    currentProfilePage === "courses"
                                        ? "opacity-100 translate-x-0 pointer-events-auto"
                                        : "opacity-0 translate-x-8 pointer-events-none absolute inset-0"
                                }`}
                            >
                                <div className="my-10">
                                    <h3 className="text-xl dark:text-white md:px-5 font-bold mb-5">
                                        {t("profile_courses")} (
                                        {coursesFromUser.length})
                                    </h3>
                                    {coursesFromUser.length == 0 && (
                                        <div className="text-gray-400 dark:text-textSecondDark mt-1 md:px-5">
                                            {t("profile_courses_empty")}
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-2 sm:px-5">
                                        {coursesFromUser.map(
                                            (course, index) => (
                                                <div
                                                    key={`formation-${course.id}`}
                                                    className="group"
                                                >
                                                    <DisplayCourse
                                                        course={course}
                                                    />
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* Options */}
                <div className="xl:col-span-4 md:px-0 px-4 lg:mt-0 mt-5">
                    <div className="lg:sticky flex flex-col gap-5 pb-10 lg:top-[72px]">
                        {/* <div className="bg-gray-50 dark:bg-box border dark:border-box-outline p-5 rounded-xl dark:text-textSecondDark text-sm">
                            <h2 className="text-xl font-bold dark:text-white mb-1">
                                {t("profile_infos")}
                            </h2>

                            <div className="mt-1">
                                {user.lastActivity &&
                                    t("profile_lastActivity", {
                                        date: moment
                                            .utc(user.lastActivity)
                                            .tz(
                                                session && session.user
                                                    ? session.user.timezone
                                                    : "Europe/Paris",
                                            )
                                            .locale(locale)
                                            .format("LLL"),
                                    })}
                            </div>
                        </div> */}

                        <div className="border dark:border-box-outline p-5 dark:text-white rounded-2xl">
                            <h3 className="dark:text-white font-bold mb-4">
                                {t("profile_current_league")}
                            </h3>
                            {props.data.currentLeague ? (
                                <div className="flex items-center gap-1.5">
                                    <Image
                                        src={`/static/icons/leagues/${props.data.currentLeague.level}.svg`}
                                        alt="League"
                                        width={16}
                                        height={16}
                                    />
                                    <span className="font-bold">
                                        {t(
                                            `leagues.${props.data.currentLeague.level}`,
                                        )}
                                    </span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded-full bg-gray-300 dark:bg-grayDark-600"></div>
                                    <span className="text-gray-500 dark:text-grayDark-400 text-sm">
                                        {t("profile_not_ranked")}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="border dark:border-box-outline p-5 dark:text-white rounded-2xl">
                            <h3 className="dark:text-white font-bold mb-4">
                                {t("profile_stats")}
                            </h3>

                            <div className="grid grid-cols-2 gap-5 text-sm">
                                <div className="flex flex-col gap-1">
                                    <div className="dark:text-textSecondDark text-gray-500">
                                        {t("profile_xp")}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <Image
                                            src="/static/icons/xp.svg"
                                            alt="XP"
                                            width={16}
                                            height={16}
                                        />
                                        <span className="font-bold">
                                            {user.xp.toLocaleString(locale) ??
                                                0}{" "}
                                            (
                                            {t.rich("profile_level_short", {
                                                level: (
                                                    <Level
                                                        key="level"
                                                        user={user}
                                                        onlyLevel
                                                    />
                                                ),
                                            })}
                                            )
                                        </span>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="dark:text-textSecondDark text-gray-500">
                                        {t("profile_fragments_count")}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <Image
                                            src="/static/icons/fragment.svg"
                                            alt="Fragments"
                                            width={16}
                                            height={16}
                                        />
                                        <span className="font-bold">
                                            {user.fragments.toLocaleString(
                                                locale,
                                            ) ?? 0}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="dark:text-textSecondDark text-gray-500">
                                        {t("profile_current_streak")}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <Image
                                            src="/static/icons/streak.svg"
                                            alt="Fragments"
                                            width={16}
                                            height={16}
                                        />
                                        <span className="font-bold">
                                            {user.streakCount}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="dark:text-textSecondDark text-gray-500">
                                        {t("profile_best_streak")}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <Image
                                            src="/static/icons/streak.svg"
                                            alt="Fragments"
                                            width={16}
                                            height={16}
                                        />
                                        <span className="font-bold">
                                            {user.maxStreakCount}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Badges */}
                        <div className="border dark:border-box-outline p-5 dark:text-white rounded-2xl">
                            <h3 className="dark:text-white font-bold mb-4 flex justify-between items-center">
                                {t("profile_badges")}
                                <div
                                    className="font-semibold text-primary text-sm cursor-pointer hover:text-primary-100 duration-100 uppercase"
                                    onClick={() => setShowBadgesModal(true)}
                                >
                                    {t("profile_show_all")}
                                </div>
                            </h3>
                            {specialBadges.length > 0 && (
                                <div className="mb-4">
                                    <div className="text-xs text-gray-500 dark:text-grayDark-400 mb-2">
                                        {t("profile_badges_special")}
                                    </div>
                                    <div className="grid grid-cols-4 lg:grid-cols-5">
                                        {specialBadges
                                            .slice(0, 5)
                                            .map((userBadge) => (
                                                <div
                                                    key={`sidebar-special-badge-${userBadge.id}`}
                                                    className="relative hover:opacity-85 duration-150 cursor-pointer"
                                                    onClick={() =>
                                                        setShowBadgesModal(true)
                                                    }
                                                    title={
                                                        userBadge.badge
                                                            .nameTranslations?.[
                                                            locale
                                                        ] ||
                                                        userBadge.badge
                                                            .nameTranslations
                                                            ?.fr
                                                    }
                                                >
                                                    <Image
                                                        src={
                                                            userBadge.badge
                                                                .imageUrl
                                                        }
                                                        alt={
                                                            userBadge.badge
                                                                .nameTranslations?.[
                                                                locale
                                                            ] ||
                                                            userBadge.badge
                                                                .nameTranslations
                                                                ?.fr ||
                                                            "Badge"
                                                        }
                                                        width={50}
                                                        height={62}
                                                        layout="responsive"
                                                        className="drop-shadow-md"
                                                    />
                                                </div>
                                            ))}
                                    </div>
                                    {monthlyBadges.length > 0 && (
                                        <div className="text-xs text-gray-500 dark:text-grayDark-400 mt-4">
                                            {t("profile_badges_monthly")}
                                        </div>
                                    )}
                                </div>
                            )}
                            <div
                                className={`grid grid-cols-4 lg:grid-cols-5 ${
                                    specialBadges.length > 0 &&
                                    monthlyBadges.length === 0
                                        ? "hidden"
                                        : ""
                                }`}
                            >
                                {monthlyBadges.length > 0 ? (
                                    monthlyBadges
                                        .slice(0, 5)
                                        .map((userBadge, index) => (
                                            <div
                                                key={`sidebar-badge-${userBadge.id}`}
                                                className={`relative hover:opacity-85 duration-150 cursor-pointer ${
                                                    index >= 4
                                                        ? "hidden lg:block"
                                                        : ""
                                                }`}
                                            >
                                                <Image
                                                    src={
                                                        userBadge.badge.imageUrl
                                                    }
                                                    alt={
                                                        userBadge.badge
                                                            .nameTranslations
                                                            ?.fr || "Badge"
                                                    }
                                                    width={50}
                                                    height={62}
                                                    layout="responsive"
                                                    className="drop-shadow-md"
                                                />
                                            </div>
                                        ))
                                ) : (
                                    <div className="col-span-4 lg:col-span-5 text-center text-gray-500 dark:text-grayDark-400 py-8">
                                        {t("profile_no_badges_yet")}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Success */}
                        <div className="border dark:border-box-outline p-5 dark:text-white rounded-2xl">
                            <h3 className="dark:text-white font-bold mb-4 flex justify-between items-center">
                                {t("profile_achievements")}
                                <div
                                    className="font-semibold text-primary text-sm cursor-pointer hover:text-primary-100 duration-100 uppercase"
                                    onClick={() =>
                                        setShowAchievementsModal(true)
                                    }
                                >
                                    {t("profile_show_all_achievements")}
                                </div>
                            </h3>
                            <div className="grid grid-cols-4 lg:grid-cols-5 gap-1.5">
                                {achievements && achievements.length > 0 ? (
                                    achievements
                                        .slice(0, 5)
                                        .map((achievement, index) => (
                                            <div
                                                key={`sidebar-achievement-${achievement.id}`}
                                                className={`relative hover:opacity-85 duration-150 cursor-pointer ${
                                                    index >= 4
                                                        ? "hidden lg:block"
                                                        : ""
                                                } ${
                                                    achievement.isLocked
                                                        ? "opacity-50 grayscale"
                                                        : ""
                                                }`}
                                            >
                                                <Image
                                                    src={achievement.imageUrl}
                                                    alt={achievement.name}
                                                    width={50}
                                                    height={62}
                                                    layout="responsive"
                                                    className="drop-shadow-md"
                                                />
                                                {/* Texte "NIVEAU X" superposé */}
                                                {!achievement.isLocked &&
                                                    achievement.level > 0 && (
                                                        <div className="absolute bottom-2 w-full">
                                                            <div className="text-[8px] text-white font-bold uppercase leading-tight tracking-wider text-center w-full">
                                                                {achievement.level ===
                                                                achievement.maxLevel
                                                                    ? t(
                                                                          "profile_level_max",
                                                                      )
                                                                    : t(
                                                                          "profile_level_long",
                                                                          {
                                                                              level: achievement.level,
                                                                          },
                                                                      )}
                                                            </div>
                                                        </div>
                                                    )}
                                                {achievement.isLocked && (
                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                        <svg
                                                            className="w-6 h-6 text-gray-400"
                                                            fill="currentColor"
                                                            viewBox="0 0 20 20"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                ) : (
                                    <div className="col-span-4 lg:col-span-5 text-center text-gray-500 dark:text-grayDark-400 py-8">
                                        {t("profile_no_achievements_yet")}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="1em"
                                height="1em"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="none"
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.5"
                                    d="M13.5 4.5a1.5 1.5 0 0 1-3 0C10.5 3.672 12 2 12 2s1.5 1.672 1.5 2.5M12 6v3m5.667 5c1.564 0 2.833-1.12 2.833-2.5S19.232 9 17.667 9H6.333C4.77 9 3.5 10.12 3.5 11.5S4.769 14 6.333 14c1.371 0 2.571-.859 2.834-2c.262 1.141 1.462 2 2.833 2c1.37 0 2.57-.859 2.833-2c.263 1.141 1.463 2 2.834 2M5 14l.52 2.58c.525 2.597.788 3.895 1.676 4.658c.889.762 2.14.762 4.643.762h.322c2.503 0 3.754 0 4.643-.762c.889-.763 1.15-2.061 1.675-4.658L19 14"
                                    color="currentColor"
                                />
                            </svg>
                            {t("profile_inscriptionDate", {
                                date: moment
                                    .utc(user.dateCreation)
                                    .tz(
                                        session && session.user
                                            ? session.user.timezone
                                            : "Europe/Paris",
                                    )
                                    .locale(locale)
                                    .format("MMM YYYY"),
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {!userLoaded ? (
                <div className="flex flex-col justify-center items-center h-screen">
                    <div className="mb-4">
                        <BebotMascot width="200px" className="mb-2" />
                    </div>
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 dark:border-white"></div>
                </div>
            ) : (
                <>
                    {session && session.user ? (
                        <div>
                            {isLoading || !user.firstName ? (
                                <div className="flex justify-center mt-9 h-screen">
                                    <SpinnerCircular
                                        size={50}
                                        thickness={150}
                                        color="#fff"
                                    />
                                </div>
                            ) : (
                                <>
                                    {isSendingMessage && (
                                        <div className="fixed top-0 left-0 w-full h-full bg-black/50 z-50 flex justify-center items-center">
                                            <div className="bg-white dark:bg-box border border-transparent dark:border-box-outline rounded-xl p-5 xl:w-5/12 lg:w-6/12 md:w-8/12 w-10/12">
                                                <div className="flex justify-between items-center">
                                                    <h1 className="font-sourceSans text-lg dark:text-white font-bold">
                                                        {t(
                                                            "profile_send_message",
                                                            {
                                                                fullname:
                                                                    user.showPseudo &&
                                                                    user.pseudo
                                                                        ? user.pseudo
                                                                        : `${user.firstName} ${user.lastName}`,
                                                            },
                                                        )}
                                                    </h1>
                                                    <Button
                                                        onClick={() =>
                                                            setIsSendingMessage(
                                                                false,
                                                            )
                                                        }
                                                        type="outline-pop"
                                                        small
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="1em"
                                                            height="1em"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <g
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="1.5"
                                                            >
                                                                <circle
                                                                    cx="12"
                                                                    cy="12"
                                                                    r="10"
                                                                ></circle>
                                                                <path
                                                                    strokeLinecap="round"
                                                                    d="m14.5 9.5l-5 5m0-5l5 5"
                                                                ></path>
                                                            </g>
                                                        </svg>
                                                    </Button>
                                                </div>
                                                <div className="mt-5">
                                                    <Editor
                                                        key={editorKey}
                                                        tinymceScriptSrc="/tinymce/tinymce.min.js"
                                                        licenseKey="gpl"
                                                        init={{
                                                            height: 150,
                                                            max_height: 300,
                                                            menubar: false,
                                                            inline: true,
                                                            codesample_languages:
                                                                process.env
                                                                    .TINYMCE_CODESAMPLE_LANGUAGES,
                                                            codesample_global_prismjs: true,
                                                            toolbar_location:
                                                                "bottom",
                                                            fixed_toolbar_container:
                                                                "#editor-toolbar",
                                                            plugins:
                                                                "codesample anchor autolink link autoresize autosave fullscreen image lists preview searchreplace table visualblocks wordcount",
                                                            toolbar:
                                                                "undo redo | bold italic forecolor | numlist bullist | image codesample | link anchor | restoredraft",
                                                            content_style:
                                                                session.user
                                                                    .darkMode
                                                                    ? ".mce-content-body {outline: none; margin: 0; max-height: 300px; overflow: auto; color: white; border: 1px solid #C4A461; border-radius: 15px; padding: 10px} .mce-content-body p {margin: 0} .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before { color: #9F9FA0; padding-left: 10px } .tox-toolbar__primary { background: transparent!important} .tox-editor-header { border: 0!important } .tox-toolbar-overlord { background-color: transparent!important } .tox-toolbar__group:not(:last-of-type) { border-right: 1px solid #9F9FA0!important }"
                                                                    : ".mce-content-body {outline: none; margin: 0; max-height: 300px; overflow: auto; border: 1px solid #C4A461; border-radius: 15px; padding: 10px} .mce-content-body p {margin: 0} .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before { padding-left: 10px }  .tox-toolbar__primary { background: transparent!important} .tox-editor-header { border: 0!important } .tox-toolbar-overlord { background-color: transparent!important } .tox-toolbar__group:not(:last-of-type) { border-right: 1px solid #9F9FA0!important }",
                                                            placeholder: t(
                                                                "profile_send_message_placeholder",
                                                            ),
                                                            skin: session.user
                                                                .darkMode
                                                                ? "oxide-dark"
                                                                : "oxide",
                                                            content_css: session.user .darkMode
                                                                ? false
                                                                : "default",
                                                            image_dimensions: false,
                                                        }}
                                                        onChange={(e) => {
                                                            setMessage(
                                                                e.target.getContent(),
                                                            );
                                                        }}
                                                    />
                                                    <div
                                                        className="editor-toolbar mt-1"
                                                        id="editor-toolbar"
                                                    ></div>
                                                </div>
                                                <div className="mt-5 flex justify-end">
                                                    <Button
                                                        small
                                                        onClick={sendMessage}
                                                    >
                                                        {t(
                                                            "profile_send_message_button",
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Modale des badges */}
                                    {showBadgesModal && (
                                        <div
                                            className="fixed top-0 left-0 w-full h-full bg-black/50 z-50 flex justify-center items-center p-4 sm:p-0"
                                            onClick={() =>
                                                setShowBadgesModal(false)
                                            }
                                        >
                                            <div
                                                className="bg-white dark:bg-box border border-transparent dark:border-box-outline rounded-xl p-4 sm:p-6 max-w-4xl w-full sm:w-11/12 max-h-[90vh] sm:max-h-[80vh] overflow-y-auto"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <div className="flex justify-between items-center mb-4 sm:mb-6">
                                                    <h2 className="text-lg sm:text-2xl font-bold dark:text-white">
                                                        {t(
                                                            "profile_badges_title",
                                                            {
                                                                name:
                                                                    user.showPseudo &&
                                                                    user.pseudo
                                                                        ? capitalize(
                                                                              user.pseudo,
                                                                          )
                                                                        : `${capitalize(
                                                                              user.firstName,
                                                                          )} ${capitalize(
                                                                              user.lastName,
                                                                          )}`,
                                                            },
                                                        )}
                                                    </h2>
                                                    <div
                                                        onClick={() =>
                                                            setShowBadgesModal(
                                                                false,
                                                            )
                                                        }
                                                        className="cursor-pointer dark:text-white"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="1em"
                                                            height="1em"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <line
                                                                x1="6"
                                                                y1="6"
                                                                x2="18"
                                                                y2="18"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                            <line
                                                                x1="18"
                                                                y1="6"
                                                                x2="6"
                                                                y2="18"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                        </svg>
                                                    </div>
                                                </div>

                                                <div className="space-y-3 sm:space-y-4">
                                                    {allBadges.length > 0 ? (
                                                        [
                                                            ...specialBadges,
                                                            ...monthlyBadges,
                                                        ].map(
                                                            (
                                                                userBadge,
                                                                index,
                                                                ordered,
                                                            ) => (
                                                                <Fragment
                                                                    key={
                                                                        userBadge.id
                                                                    }
                                                                >
                                                                {specialBadges.length >
                                                                    0 &&
                                                                    (index ===
                                                                        0 ||
                                                                        !!ordered[
                                                                            index -
                                                                                1
                                                                        ].badge
                                                                            ?.isManual !==
                                                                            !!userBadge
                                                                                .badge
                                                                                ?.isManual) && (
                                                                        <h3
                                                                            className={`text-sm font-semibold text-gray-500 dark:text-grayDark-400 mb-0 ${
                                                                                index ===
                                                                                0
                                                                                    ? ""
                                                                                    : "pt-3"
                                                                            }`}
                                                                        >
                                                                            {userBadge
                                                                                .badge
                                                                                ?.isManual
                                                                                ? t(
                                                                                      "profile_badges_special",
                                                                                  )
                                                                                : t(
                                                                                      "profile_badges_monthly",
                                                                                  )}
                                                                        </h3>
                                                                    )}
                                                                <div
                                                                    className="bg-white dark:bg-bgDark border border-gray-200 dark:border-borderDark rounded-2xl p-3 sm:p-5 hover:shadow-md dark:hover:bg-bgDark-300 transition-all duration-200"
                                                                >
                                                                    <div className="flex items-center gap-3 sm:gap-4">
                                                                        {/* Badge */}
                                                                        <div className="flex-shrink-0 relative">
                                                                            <div className="size-24 relative">
                                                                                <Image
                                                                                    src={
                                                                                        userBadge
                                                                                            .badge
                                                                                            .imageUrl
                                                                                    }
                                                                                    alt={
                                                                                        userBadge
                                                                                            .badge
                                                                                            .nameTranslations
                                                                                            ?.fr ||
                                                                                        "Badge"
                                                                                    }
                                                                                    width={
                                                                                        100
                                                                                    }
                                                                                    height={
                                                                                        100
                                                                                    }
                                                                                    className="drop-shadow-lg"
                                                                                />
                                                                            </div>
                                                                        </div>

                                                                        {/* Contenu */}
                                                                        <div className="flex-1">
                                                                            <div className="flex items-center justify-between mb-2">
                                                                                <h3 className="font-bold text-base sm:text-lg text-gray-900 dark:text-white">
                                                                                    {userBadge
                                                                                        .badge
                                                                                        .nameTranslations?.[
                                                                                        locale
                                                                                    ] ||
                                                                                        userBadge
                                                                                            .badge
                                                                                            .nameTranslations
                                                                                            ?.fr ||
                                                                                        "Badge"}
                                                                                </h3>
                                                                                <span className="text-xs text-gray-500 dark:text-grayDark-400">
                                                                                    {moment(
                                                                                        userBadge.awardedAt,
                                                                                    )
                                                                                        .locale(
                                                                                            locale,
                                                                                        )
                                                                                        .format(
                                                                                            "LL",
                                                                                        )}
                                                                                </span>
                                                                            </div>

                                                                            <div className="text-sm text-gray-600 dark:text-textSecondDark mb-0">
                                                                                {userBadge
                                                                                    .badge
                                                                                    .isManual ? (
                                                                                    <>
                                                                                        <div className="mb-1">
                                                                                            {userBadge
                                                                                                .badge
                                                                                                .descriptionTranslations?.[
                                                                                                locale
                                                                                            ] ||
                                                                                                userBadge
                                                                                                    .badge
                                                                                                    .descriptionTranslations
                                                                                                    ?.fr}
                                                                                        </div>
                                                                                        {userBadge.note && (
                                                                                            <div className="mt-2 pl-3 border-l-2 border-gray-200 dark:border-borderDark text-gray-700 dark:text-textDark whitespace-pre-line">
                                                                                                {
                                                                                                    userBadge.note
                                                                                                }
                                                                                            </div>
                                                                                        )}
                                                                                    </>
                                                                                ) : userBadge
                                                                                      .badge
                                                                                      .isSpecialEvent ? (
                                                                                    `Badge spécial de ${userBadge.badge.month}/${userBadge.badge.year}`
                                                                                ) : (
                                                                                    <>
                                                                                        <div className="mb-1">
                                                                                            {t(
                                                                                                "profile_badge_of",
                                                                                                {
                                                                                                    month: moment()
                                                                                                        .month(
                                                                                                            userBadge
                                                                                                                .badge
                                                                                                                .month -
                                                                                                                1,
                                                                                                        )
                                                                                                        .locale(
                                                                                                            locale,
                                                                                                        )
                                                                                                        .format(
                                                                                                            "MMMM",
                                                                                                        )
                                                                                                        .toLowerCase(),
                                                                                                    year: userBadge
                                                                                                        .badge
                                                                                                        .year,
                                                                                                },
                                                                                            )}
                                                                                        </div>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                </Fragment>
                                                            ),
                                                        )
                                                    ) : (
                                                        <div className="text-center py-8">
                                                            <p className="text-gray-500 dark:text-grayDark-400">
                                                                {t(
                                                                    "profile_no_badges_yet",
                                                                )}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Modale des succès */}
                                    {showAchievementsModal && (
                                        <div
                                            className="fixed top-0 left-0 w-full h-full bg-black/50 z-50 flex justify-center items-center p-4 sm:p-0"
                                            onClick={() =>
                                                setShowAchievementsModal(false)
                                            }
                                        >
                                            <div
                                                className="bg-white dark:bg-box border border-transparent dark:border-box-outline rounded-xl p-4 sm:p-6 max-w-4xl w-full sm:w-11/12 max-h-[90vh] sm:max-h-[80vh] overflow-y-auto"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <div className="flex justify-between items-center mb-4 sm:mb-6">
                                                    <h2 className="text-lg sm:text-2xl font-bold dark:text-white">
                                                        {t(
                                                            "profile_achievements_title",
                                                            {
                                                                name:
                                                                    user.showPseudo &&
                                                                    user.pseudo
                                                                        ? capitalize(
                                                                              user.pseudo,
                                                                          )
                                                                        : `${capitalize(
                                                                              user.firstName,
                                                                          )} ${capitalize(
                                                                              user.lastName,
                                                                          )}`,
                                                            },
                                                        )}
                                                    </h2>
                                                    <div
                                                        onClick={() =>
                                                            setShowAchievementsModal(
                                                                false,
                                                            )
                                                        }
                                                        className="cursor-pointer dark:text-white"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="1em"
                                                            height="1em"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <line
                                                                x1="6"
                                                                y1="6"
                                                                x2="18"
                                                                y2="18"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                            <line
                                                                x1="18"
                                                                y1="6"
                                                                x2="6"
                                                                y2="18"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                        </svg>
                                                    </div>
                                                </div>

                                                <div className="space-y-3 sm:space-y-4">
                                                    {achievements &&
                                                    achievements.length > 0 ? (
                                                        achievements.map(
                                                            (achievement) => (
                                                                <div
                                                                    key={
                                                                        achievement.id
                                                                    }
                                                                    className={`bg-white dark:bg-bgDark border border-gray-200 dark:border-borderDark rounded-2xl p-3 sm:p-5 hover:shadow-md dark:hover:bg-bgDark-300 transition-all duration-200 ${
                                                                        achievement.isLocked
                                                                            ? "opacity-60"
                                                                            : ""
                                                                    }`}
                                                                >
                                                                    <div className="flex items-center gap-3 sm:gap-4">
                                                                        <div className="flex-shrink-0 relative">
                                                                            <div className="w-12 h-15 sm:w-16 sm:h-20 relative">
                                                                                <Image
                                                                                    src={
                                                                                        achievement.imageUrl
                                                                                    }
                                                                                    alt={
                                                                                        achievement.name
                                                                                    }
                                                                                    width={
                                                                                        64
                                                                                    }
                                                                                    height={
                                                                                        80
                                                                                    }
                                                                                    className={`drop-shadow-lg ${
                                                                                        achievement.isLocked
                                                                                            ? "grayscale"
                                                                                            : ""
                                                                                    }`}
                                                                                />
                                                                                {!achievement.isLocked &&
                                                                                    achievement.level >
                                                                                        0 && (
                                                                                        <div className="absolute bottom-2 w-full">
                                                                                            <div className="text-[8px] text-white font-bold uppercase leading-tight tracking-wider text-center w-full">
                                                                                                {achievement.level ===
                                                                                                achievement.maxLevel
                                                                                                    ? t(
                                                                                                          "profile_level_max",
                                                                                                      )
                                                                                                    : t(
                                                                                                          "profile_level_long",
                                                                                                          {
                                                                                                              level: achievement.level,
                                                                                                          },
                                                                                                      )}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                {achievement.isLocked && (
                                                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                                                        <svg
                                                                                            className="w-6 h-6 text-gray-400"
                                                                                            fill="currentColor"
                                                                                            viewBox="0 0 20 20"
                                                                                        >
                                                                                            <path
                                                                                                fillRule="evenodd"
                                                                                                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                                                                                clipRule="evenodd"
                                                                                            />
                                                                                        </svg>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>

                                                                        {/* Contenu */}
                                                                        <div className="flex-1">
                                                                            <div className="flex items-center justify-between mb-2">
                                                                                <h3 className="font-bold text-base sm:text-lg text-gray-900 dark:text-white">
                                                                                    {
                                                                                        achievement.name
                                                                                    }
                                                                                </h3>
                                                                                <div className="text-right">
                                                                                    {!achievement.isLocked &&
                                                                                        achievement.level >
                                                                                            0 && (
                                                                                            <span className="text-sm font-semibold text-primary">
                                                                                                {t(
                                                                                                    "profile_level_short",
                                                                                                    {
                                                                                                        level: achievement.level,
                                                                                                    },
                                                                                                )}

                                                                                                /
                                                                                                {
                                                                                                    achievement.maxLevel
                                                                                                }
                                                                                            </span>
                                                                                        )}
                                                                                    {achievement.progress &&
                                                                                        achievement.level <
                                                                                            achievement.maxLevel && (
                                                                                            <div className="text-xs text-gray-500 dark:text-textSecondDark">
                                                                                                {
                                                                                                    achievement
                                                                                                        .progress
                                                                                                        .current
                                                                                                }

                                                                                                /
                                                                                                {
                                                                                                    achievement
                                                                                                        .progress
                                                                                                        .target
                                                                                                }
                                                                                            </div>
                                                                                        )}
                                                                                </div>
                                                                            </div>

                                                                            <p className="text-sm text-gray-600 dark:text-textSecondDark mb-3">
                                                                                {
                                                                                    achievement.description
                                                                                }
                                                                            </p>

                                                                            {/* Barre de progression */}
                                                                            {achievement.progress &&
                                                                                achievement.level <
                                                                                    achievement.maxLevel &&
                                                                                !achievement.isLocked && (
                                                                                    <div className="relative">
                                                                                        <div className="w-full bg-gray-200 dark:bg-bgDark-300 rounded-full h-2">
                                                                                            <div
                                                                                                className="h-2 rounded-full bg-gradient-to-r from-primary to-primary-100 transition-all duration-300"
                                                                                                style={{
                                                                                                    width: `${achievement.progress.percentage}%`,
                                                                                                }}
                                                                                            ></div>
                                                                                        </div>
                                                                                    </div>
                                                                                )}

                                                                            {/* Badge "Complété" pour les achievements au niveau max */}
                                                                            {achievement.level ===
                                                                                achievement.maxLevel &&
                                                                                !achievement.isLocked && (
                                                                                    <div className="mt-2">
                                                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                                                                            ✓{" "}
                                                                                            {t(
                                                                                                "profile_completed",
                                                                                            )}
                                                                                        </span>
                                                                                    </div>
                                                                                )}

                                                                            {/* Badge "Verrouillé" */}
                                                                            {achievement.isLocked && (
                                                                                <div className="mt-2">
                                                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-grayDark-800 dark:text-grayDark-400">
                                                                                        🔒{" "}
                                                                                        {t(
                                                                                            "profile_locked",
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ),
                                                        )
                                                    ) : (
                                                        <div className="text-center py-8">
                                                            <p className="text-gray-500 dark:text-grayDark-400">
                                                                {t(
                                                                    "profile_no_achievements_yet",
                                                                )}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {content}
                                    <Footer />
                                </>
                            )}
                        </div>
                    ) : (
                        <div>
                            {isLoading || !user.firstName ? (
                                <div className="flex justify-center mt-9">
                                    <SpinnerCircular
                                        size={50}
                                        thickness={150}
                                        color="#fff"
                                    />
                                </div>
                            ) : (
                                <>{content}</>
                            )}
                        </div>
                    )}
                </>
            )}
        </>
    );
}
